import { createContext, useMemo } from 'react';
import type { JSX } from 'react';

import { isEqual } from 'lodash';

import { getDocumentHash, getDocumentName, graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';

import { useMemoCompare } from '../../hooks/useMemoCompare.hook';
import { Loading } from '../../components/Loading';
import { environment } from '../../environment';
import { QUERY_META_DEHYDRATE } from '../../components/PersistQueryClientProvider';
import { useGraphQLQuery } from '../../hooks/useGraphQLQuery.hook';

import { useReferencesRevisionCheck } from './hooks/useReferencesRevisionCheck.hook';
import { referencesMapper } from './utils/mapper.util';
import type {
  ReferencesContext_AccountingState,
  ReferencesContext_AccountingWorkflow,
  ReferencesContext_ActorCategory,
  ReferencesContext_ActorType,
  ReferencesContext_AddressType,
  ReferencesContext_AddressableConfig,
  ReferencesContext_ArchiveBinder,
  ReferencesContext_BusinessEventFileCategory,
  ReferencesContext_BusinessEventState,
  ReferencesContext_BusinessEventType,
  ReferencesContext_BusinessEventVenue,
  ReferencesContext_BusinessEventWorkflow,
  ReferencesContext_Civility,
  ReferencesContext_CodingStrategy,
  ReferencesContext_ConstructionType,
  ReferencesContext_ContentOrigin,
  ReferencesContext_Context,
  ReferencesContext_CostFileCategory,
  ReferencesContext_CostMargin,
  ReferencesContext_CostType,
  ReferencesContext_Currency,
  ReferencesContext_Destination,
  ReferencesContext_DistanceRadius,
  ReferencesContext_DocumentTemplateFileCategory,
  ReferencesContext_DocumentType,
  ReferencesContext_EdmDomain,
  ReferencesContext_EntityRelationshipStrategy,
  ReferencesContext_FileDirectoryCategory,
  ReferencesContext_FileDirectoryType,
  ReferencesContext_FileOrigin,
  ReferencesContext_FolderFileCategory,
  ReferencesContext_FolderState,
  ReferencesContext_FolderWorkflow,
  ReferencesContext_GenericStatus,
  ReferencesContext_LayerSource,
  ReferencesContext_MaterialFileCategory,
  ReferencesContext_MissionCategory,
  ReferencesContext_MissionFileCategory,
  ReferencesContext_MissionState,
  ReferencesContext_MissionWorkflow,
  ReferencesContext_NumberConstraint,
  ReferencesContext_OrganizationFunction,
  ReferencesContext_PaletteMode,
  ReferencesContext_Pathology,
  ReferencesContext_PaymentMethod,
  ReferencesContext_PaymentTerm,
  ReferencesContext_PennylaneUserAccount,
  ReferencesContext_PostalAddressStandard,
  ReferencesContext_ExternalReferenceType,
  ReferencesContext_PrintStyle,
  ReferencesContext_Projection,
  ReferencesContext_QuotationFileCategory,
  ReferencesContext_QuotationState,
  ReferencesContext_QuotationWorkflow,
  ReferencesContext_Reference,
  ReferencesContext_ReportFileCategory,
  ReferencesContext_ReportState,
  ReferencesContext_ReportWorkflow,
  ReferencesContext_RepresentativeType,
  ReferencesContext_ResolvableMissionTypeFileCategory,
  ReferencesContext_ResolvableServiceFileCategory,
  ReferencesContext_Resolver,
  ReferencesContext_ResolverGroup,
  ReferencesContext_ResolverReturnType,
  ReferencesContext_SectionTemplateFileCategory,
  ReferencesContext_SectionType,
  ReferencesContext_StorageStrategy,
  ReferencesContext_ThirdPartyCategory,
  ReferencesContext_ThirdPartyFileCategory,
  ReferencesContext_ThirdPartyType,
  ReferencesContext_TimeUnit,
  ReferencesContext_Toolkit,
  ReferencesContext_Unit,
  ReferencesContext_UserAction,
  ReferencesContext_UserFileCategory,
  ReferencesContext_UserRole,
  ReferencesContext_ValidityPeriod,
  ReferencesContext_Vat,
  MappedReferencesContext as ReferencesData,
} from './types/mapped-references-context.type';

export * from './types/mapped-references-context.type';

export const ReferencesContext_QueryDocument = graphql(`
  query ReferencesContext_Query {
    accountingStates: findAllActiveAccountingStates {
      _id
      code
      version
      index
      label
      chip {
        color
        icon
      }
      description
      isSelectedByDefault
    }
    accountingWorkflows: findAllActiveAccountingWorkflows {
      _id
      code
      version
      index
      label
      description
      isSelectedByDefault
      isEnabled
      isTechnical
      stateMachine {
        _id
        stateId
        isStartingPoint
        isEndPoint
        compatibilities {
          mission {
            multiplicity
            stateIds
          }
          quotation {
            multiplicity
            stateIds
          }
        }
        actions {
          _id
          description
          label
          nextNodeId
          roleRequirements {
            dispatchAction
          }
          settings {
            generatePennylaneQuote
            checkQuotationValidity
            showServiceUsageAggregationDialog
          }
        }
        roleRequirements {
          read
        }
      }
      initAction {
        _id
        description
        label
        nextNodeId
        roleRequirements {
          dispatchAction
        }
        settings {
          generatePennylaneQuote
          checkQuotationValidity
          showServiceUsageAggregationDialog
        }
      }
      compatibilities {
        missionWorkflowIds
      }
    }
    actorCategories: findAllActiveActorCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      isInternal
      isExternal
    }
    actorTypes: findAllActiveActorTypes {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    addressTypes: findAllActiveAddressTypes {
      _id
      code
      version
      index
      label
      chip {
        color
        icon
      }
      isSelectedByDefault
      isPennylaneMainAddress
      isPennylaneDeliveryAddress
      requiresPostalInfo
      requiresLocationInfo
      requiresCadastralInfo
    }
    addressableConfigs: findAllActiveAddressableConfigs {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      isOptional
      allowedTypeIds
      allowMultipleAddresses
      rules {
        multiplicity
        typeId
      }
      pennylaneMainAddressMultiplicity
      pennylaneDeliveryAddressMultiplicity
      usedByDistanceCalculatorMultiplicity
    }
    archiveBinders: findAllActiveArchiveBinders {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    businessEventFileCategories: findAllActiveBusinessEventFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
      compatibleWorkflowIds
    }
    businessEventStates: findAllActiveBusinessEventStates {
      _id
      code
      version
      index
      label
      description
      chip {
        color
        icon
      }
      isSelectedByDefault
    }
    businessEventTypes: findAllActiveBusinessEventTypes {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    businessEventVenues: findAllActiveBusinessEventVenues {
      _id
      code
      version
      index
      label
      description
      chip {
        color
        icon
      }
      isSelectedByDefault
    }
    businessEventWorkflows: findAllActiveBusinessEventWorkflows {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      description
      isEnabled
      isTechnical
      stateMachine {
        _id
        stateId
        isStartingPoint
        isEndPoint
        compatibilities {
          folder {
            multiplicity
            stateIds
          }
          mission {
            multiplicity
            stateIds
          }
        }
        settings {
          allowsFileUpload
          allowsFileDownload
        }
        actions {
          _id
          description
          label
          nextNodeId
          roleRequirements {
            dispatchAction
          }
          settings {
            refreshFileDirectoryGates
          }
        }
        roleRequirements {
          read
        }
      }
      initAction {
        _id
        description
        label
        nextNodeId
        roleRequirements {
          dispatchAction
        }
        settings {
          refreshFileDirectoryGates
        }
      }
      settings {
        actorableRules {
          atLeastOneInternal
          atMostOneInternal
          requiredInternalContextualRoleIds
          atLeastOneExternal
          requiredQuotationEmitterRepresentativeTypeIds
          requiredQuotationReceiverRepresentativeTypeIds
          atLeastOneRepresentativeForQuotationReceiver
          atLeastOneRepresentativeForEachExternalOrganization
          oneAndOnlyOneEdmDomain
          oneAndOnlyOneQuotationReceiver
          oneAndOnlyOneQuotationEmitter
          allowsInternalQuotationReceiver
          giveBaseRoleToAllInternalUsers
          giveBaseRoleToAllRelatedInternalOrganizationMembers
          giveBaseRoleToAllRelatedExternalOrganizationMembers
        }
      }
      compatibilities {
        folderWorkflowIds
        missionWorkflowIds
      }
    }
    civilities: findAllActiveCivilities {
      _id
      code
      version
      index
      label
      abbreviation
      isPlural
      useWhenUnknown
      isSelectedByDefault
    }
    codingStrategies: findAllActiveCodingStrategies {
      _id
      code
      version
      index
      label
      chip {
        color
        icon
      }
      isSelectedByDefault
    }
    constructionTypes: findAllActiveConstructionTypes {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    contentOrigins: findAllActiveContentOrigins {
      _id
      code
      version
      index
      label
      chip {
        color
        icon
      }
      isSelectedByDefault
    }
    contexts: findAllActiveContexts {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    costFileCategories: findAllActiveCostFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
    }
    costMargins: findAllActiveCostMargins {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      costTypeId
      fixedValue {
        value
        currencyId
      }
      variableValue
    }
    costTypes: findAllActiveCostTypes {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    currencies: findAllActiveCurrencies {
      _id
      code
      version
      index
      label
      isoCode
      symbol
      isSelectedByDefault
    }
    destinations: findAllActiveDestinations {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    distanceRadius: findAllActiveDistanceRadius {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      distanceInMeters
    }
    documentTemplateFileCategories: findAllActiveDocumentTemplateFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
    }
    documentTypes: findAllActiveDocumentTypes {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    edmDomains: findAllActiveEdmDomains {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    entityRelationshipStrategies: findAllActiveEntityRelationshipStrategies {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    fileDirectoryCategories: findAllActiveFileDirectoryCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      directoryType
    }
    fileDirectoryTypes: findAllActiveFileDirectoryTypes {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    fileOrigins: findAllActiveFileOrigins {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    folderFileCategories: findAllActiveFolderFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
      compatibleWorkflowIds
    }
    folderStates: findAllActiveFolderStates {
      _id
      code
      version
      index
      label
      chip {
        color
        icon
      }
      description
      isSelectedByDefault
    }
    folderWorkflows: findAllActiveFolderWorkflows {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      description
      isEnabled
      isTechnical
      stateMachine {
        _id
        stateId
        isStartingPoint
        isEndPoint
        compatibilities {
          mission {
            multiplicity
            stateIds
          }
          businessEvent {
            multiplicity
            stateIds
          }
        }
        settings {
          allowsFileUpload
          allowsFileDownload
        }
        actions {
          _id
          description
          label
          nextNodeId
          roleRequirements {
            dispatchAction
          }
          settings {
            refreshFileDirectoryGates
            needsCompleteArchiveInfo
          }
        }
        roleRequirements {
          read
        }
      }
      initAction {
        _id
        description
        label
        nextNodeId
        roleRequirements {
          dispatchAction
        }
        settings {
          refreshFileDirectoryGates
          needsCompleteArchiveInfo
        }
      }
      settings {
        codePrefix
        actorableRules {
          atLeastOneInternal
          atMostOneInternal
          requiredInternalContextualRoleIds
          atLeastOneExternal
          requiredQuotationEmitterRepresentativeTypeIds
          requiredQuotationReceiverRepresentativeTypeIds
          atLeastOneRepresentativeForQuotationReceiver
          atLeastOneRepresentativeForEachExternalOrganization
          oneAndOnlyOneEdmDomain
          oneAndOnlyOneQuotationReceiver
          oneAndOnlyOneQuotationEmitter
          allowsInternalQuotationReceiver
          giveBaseRoleToAllInternalUsers
          giveBaseRoleToAllRelatedInternalOrganizationMembers
          giveBaseRoleToAllRelatedExternalOrganizationMembers
        }
      }
    }
    genericStatuses: findAllActiveGenericStatuses {
      _id
      code
      version
      index
      label
      chip {
        color
        icon
      }
      isSelectedByDefault
    }
    layerSources: findAllActiveLayerSources {
      _id
      code
      version
      index
      label
      description
      isSelectedByDefault
    }
    materialFileCategories: findAllActiveMaterialFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
    }
    missionCategories: findAllMissionCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      parentId
    }
    missionFileCategories: findAllActiveMissionFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
      compatibleWorkflowIds
    }
    missionStates: findAllActiveMissionStates {
      _id
      code
      version
      index
      label
      chip {
        color
        icon
      }
      description
      isSelectedByDefault
    }
    missionWorkflows: findAllActiveMissionWorkflows {
      _id
      category
      code
      version
      index
      label
      isSelectedByDefault
      description
      isEnabled
      isTechnical
      stateMachine {
        _id
        stateId
        isStartingPoint
        isEndPoint
        compatibilities {
          folder {
            multiplicity
            stateIds
          }
          businessEvent {
            multiplicity
            stateIds
          }
          quotation {
            multiplicity
            stateIds
          }
          report {
            multiplicity
            stateIds
          }
          accounting {
            multiplicity
            stateIds
          }
        }
        settings {
          allowsFileUpload
          allowsFileDownload
        }
        actions {
          _id
          description
          label
          nextNodeId
          roleRequirements {
            dispatchAction
          }
          settings {
            needsCompleteArchiveInfo
            refreshFileDirectoryGates
          }
        }
        roleRequirements {
          read
          readAmounts
          readCosts
        }
      }
      initAction {
        _id
        description
        label
        nextNodeId
        roleRequirements {
          dispatchAction
        }
        settings {
          needsCompleteArchiveInfo
          refreshFileDirectoryGates
        }
      }
      settings {
        codePrefix
        actorableRules {
          atLeastOneInternal
          atMostOneInternal
          requiredInternalContextualRoleIds
          atLeastOneExternal
          requiredQuotationEmitterRepresentativeTypeIds
          requiredQuotationReceiverRepresentativeTypeIds
          atLeastOneRepresentativeForQuotationReceiver
          atLeastOneRepresentativeForEachExternalOrganization
          oneAndOnlyOneEdmDomain
          oneAndOnlyOneQuotationReceiver
          oneAndOnlyOneQuotationEmitter
          allowsInternalQuotationReceiver
          giveBaseRoleToAllInternalUsers
          giveBaseRoleToAllRelatedInternalOrganizationMembers
          giveBaseRoleToAllRelatedExternalOrganizationMembers
        }
        subMissionActorCase
      }
      compatibilities {
        folderWorkflowIds
      }
    }
    numberConstraints: findAllActiveNumberConstraints {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    organizationFunctions: findAllActiveOrganizationFunctions {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    paletteModes: findAllActivePaletteModes {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    pathologies: findAllActivePathologies {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    paymentMethods: findAllActivePaymentMethods {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    paymentTerms: findAllActivePaymentTerms {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    pennylaneUserAccounts: findAllActivePennylaneUserAccounts {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      isEnabled
    }
    postalAddressStandards: findAllActivePostalAddressStandards {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    externalReferenceTypes: findAllActiveExternalReferenceTypes {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      documentLabel
    }
    printStyles: findAllActivePrintStyles {
      _id
      code
      version
      index
      label
      relatedOrganizationId
      logoImageUri
      logoImageBase64
      footerLineOne
      footerLineTwo
      footerLineThree
      primaryColor
      isSelectedByDefault
    }
    projections: findAllActiveProjections {
      _id
      code
      geoportalCode
      label
      description
      remarks
      scope
      areaOfUse
      coordinateSystem
      method
      readMore
      proj4Def
      isSelectedByDefault
      version
      index
    }
    quotationFileCategories: findAllActiveQuotationFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
    }
    quotationStates: findAllActiveQuotationStates {
      _id
      code
      version
      index
      label
      chip {
        color
        icon
      }
      description
      isSelectedByDefault
    }
    quotationWorkflows: findAllActiveQuotationWorkflows {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      description
      isEnabled
      isTechnical
      stateMachine {
        _id
        stateId
        isStartingPoint
        isEndPoint
        compatibilities {
          mission {
            multiplicity
            stateIds
          }
          accounting {
            multiplicity
            stateIds
          }
        }
        settings {
          isRelatedDocumentEditable
          areMissionFieldsRelatedToQuotationEditable
          areOptionGroupsSelectionEditable
          useAsDummyForServicesInfo
          serviceUsagesSource
        }
        actions {
          _id
          description
          label
          nextNodeId
          roleRequirements {
            dispatchAction
          }
          settings {
            autoCreateDocumentIfNotExists
            autoPrint
            checkQuotationValidity
            freezeMissionInfo
            freezeQuotationInfo
            showProofFileDialog
            defaultProofFileCategoryId
            askOptionGroupsSelection
          }
        }
        roleRequirements {
          read
        }
      }
      initAction {
        _id
        description
        label
        nextNodeId
        roleRequirements {
          dispatchAction
        }
        settings {
          autoCreateDocumentIfNotExists
          autoPrint
          checkQuotationValidity
          freezeMissionInfo
          freezeQuotationInfo
          showProofFileDialog
          defaultProofFileCategoryId
          askOptionGroupsSelection
        }
      }
      settings {
        codePrefix
      }
      compatibilities {
        missionWorkflowIds
      }
    }
    references: findAllReferences {
      _id
      code
      activeVersion
      index
      label
      isSelectedByDefault
    }
    reportFileCategories: findAllActiveReportFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
    }
    reportStates: findAllActiveReportStates {
      _id
      code
      version
      index
      label
      chip {
        color
        icon
      }
      description
      isSelectedByDefault
    }
    reportWorkflows: findAllActiveReportWorkflows {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      description
      isEnabled
      isTechnical
      stateMachine {
        _id
        stateId
        isStartingPoint
        isEndPoint
        compatibilities {
          mission {
            multiplicity
            stateIds
          }
        }
        settings {
          isRelatedDocumentEditable
          useAsDummyForToolkitsInfo
        }
        actions {
          _id
          description
          label
          nextNodeId
          roleRequirements {
            dispatchAction
          }
          settings {
            autoCreateDocumentIfNotExists
            autoPrint
            freezeMissionInfo
          }
        }
        roleRequirements {
          read
        }
      }
      initAction {
        _id
        description
        label
        nextNodeId
        roleRequirements {
          dispatchAction
        }
        settings {
          autoCreateDocumentIfNotExists
          autoPrint
          freezeMissionInfo
        }
      }
      settings {
        codePrefix
      }
      compatibilities {
        missionWorkflowIds
      }
    }
    representativeTypes: findAllActiveRepresentativeTypes {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      isInternal
      isExternal
    }
    resolvableMissionTypeFileCategories: findAllActiveResolvableMissionTypeFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
    }
    resolvableServiceFileCategories: findAllActiveResolvableServiceFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
    }
    resolverGroups: findAllActiveDocumentResolverGroups {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    resolverReturnTypes: findAllActiveDocumentResolverReturnTypes {
      _id
      code
      version
      index
      label
      typeName
      isSelectedByDefault
    }
    resolvers: findAllActiveDocumentResolvers {
      _id
      code
      label
      version
      index
      command
      description
      groupId
      returnTypeId
      enableDirectGeneration
      isResolvableOnFront
      isResolvableOnBack
      isSelectedByDefault
    }
    sectionTemplateFileCategories: findAllActiveSectionTemplateFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
    }
    sectionTypes: findAllActiveSectionTypes {
      _id
      code
      version
      index
      label
      needsPrintFeedbacks
      supportPageCount
      hideHeaders
      hideFooters
      oneChildMax
      forcePagebreaks
      isSelectedByDefault
    }
    storageStrategies: findAllActiveStorageStrategies {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      readPriority
    }
    thirdPartyCategories: findAllActiveThirdPartyCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      isInternal
      isExternal
    }
    thirdPartyFileCategories: findAllActiveThirdPartyFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
    }
    thirdPartyTypes: findAllActiveThirdPartyTypes {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    timeUnits: findAllActiveTimeUnits {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    toolkits: findAllActiveToolkits {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      uriSegment
    }
    units: findAllActiveUnits {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      unit
    }
    userActions: findAllActiveUserActions {
      _id
      code
      version
      index
      label
      isSelectedByDefault
    }
    userFileCategories: findAllActiveUserFileCategories {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      hideInFileExplorer
      parentCategoryId
      pathSegment
      roleRequirementsToRead
      roleRequirementsToWrite
      hashFileName
      addTimestampToFileName
      addRandomStringToFileName
    }
    userRoles: findAllActiveUserRoles {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      isGlobal
      isContextual
      permissionCodes
    }
    validityPeriods: findAllActiveValidityPeriods {
      _id
      code
      version
      index
      label
      isSelectedByDefault
      durationInDays
    }
    vats: findAllActiveVats {
      _id
      code
      version
      index
      label
      value
      isSelectedByDefault
    }
    workflowFamilies: findAllActiveWorkflowFamilies {
      _id
      code
      version
      index
      label
      description
      isSelectedByDefault
      missionWorkflowIds
      quotationWorkflowIds
      accountingWorkflowIds
      reportWorkflowIds
    }
  }
`);

const ReferencesContext = createContext<ReferencesData | null>(null);

interface Props {
  readonly children: JSX.Element | readonly JSX.Element[];
}

const referenceQuerySettings = {
  meta: { [QUERY_META_DEHYDRATE]: true },
  gcTime: environment.referenceQueryGcTime,
  staleTime: environment.referenceQueryStaleTime,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
};

const ReferencesContextProvider = ({
  children,
}: Readonly<Props>): JSX.Element => {
  const { isReadyToRender } = useReferencesRevisionCheck(
    getDocumentName(ReferencesContext_QueryDocument),
    getDocumentHash(ReferencesContext_QueryDocument),
  );

  const { data, isSuccess } = useGraphQLQuery(
    ReferencesContext_QueryDocument,
    undefined,
    referenceQuerySettings,
  );

  const context = {
    accountingStates: (data?.accountingStates ??
      EMPTY_ARRAY) as readonly ReferencesContext_AccountingState[],
    accountingWorkflows: (data?.accountingWorkflows ??
      EMPTY_ARRAY) as readonly ReferencesContext_AccountingWorkflow[],
    actorCategories: (data?.actorCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_ActorCategory[],
    actorTypes: (data?.actorTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_ActorType[],
    addressTypes: (data?.addressTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_AddressType[],
    addressableConfigs: (data?.addressableConfigs ??
      EMPTY_ARRAY) as readonly ReferencesContext_AddressableConfig[],
    archiveBinders: (data?.archiveBinders ??
      EMPTY_ARRAY) as readonly ReferencesContext_ArchiveBinder[],
    businessEventFileCategories: (data?.businessEventFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_BusinessEventFileCategory[],
    businessEventStates: (data?.businessEventStates ??
      EMPTY_ARRAY) as readonly ReferencesContext_BusinessEventState[],
    businessEventTypes: (data?.businessEventTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_BusinessEventType[],
    businessEventVenues: (data?.businessEventVenues ??
      EMPTY_ARRAY) as readonly ReferencesContext_BusinessEventVenue[],
    businessEventWorkflows: (data?.businessEventWorkflows ??
      EMPTY_ARRAY) as readonly ReferencesContext_BusinessEventWorkflow[],
    civilities: (data?.civilities ??
      EMPTY_ARRAY) as readonly ReferencesContext_Civility[],
    codingStrategies: (data?.codingStrategies ??
      EMPTY_ARRAY) as readonly ReferencesContext_CodingStrategy[],
    constructionTypes: (data?.constructionTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_ConstructionType[],
    contentOrigins: (data?.contentOrigins ??
      EMPTY_ARRAY) as readonly ReferencesContext_ContentOrigin[],
    contexts: (data?.contexts ??
      EMPTY_ARRAY) as readonly ReferencesContext_Context[],
    costFileCategories: (data?.costFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_CostFileCategory[],
    costMargins: (data?.costMargins ??
      EMPTY_ARRAY) as readonly ReferencesContext_CostMargin[],
    costTypes: (data?.costTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_CostType[],
    currencies: (data?.currencies ??
      EMPTY_ARRAY) as readonly ReferencesContext_Currency[],
    destinations: (data?.destinations ??
      EMPTY_ARRAY) as readonly ReferencesContext_Destination[],
    distanceRadius: (data?.distanceRadius ??
      EMPTY_ARRAY) as readonly ReferencesContext_DistanceRadius[],
    documentTemplateFileCategories: (data?.documentTemplateFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_DocumentTemplateFileCategory[],
    documentTypes: (data?.documentTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_DocumentType[],
    edmDomains: (data?.edmDomains ??
      EMPTY_ARRAY) as readonly ReferencesContext_EdmDomain[],
    entityRelationshipStrategies: (data?.entityRelationshipStrategies ??
      EMPTY_ARRAY) as readonly ReferencesContext_EntityRelationshipStrategy[],
    fileDirectoryCategories: (data?.fileDirectoryCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_FileDirectoryCategory[],
    fileDirectoryTypes: (data?.fileDirectoryTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_FileDirectoryType[],
    fileOrigins: (data?.fileOrigins ??
      EMPTY_ARRAY) as readonly ReferencesContext_FileOrigin[],
    folderFileCategories: (data?.folderFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_FolderFileCategory[],
    folderStates: (data?.folderStates ??
      EMPTY_ARRAY) as readonly ReferencesContext_FolderState[],
    folderWorkflows: (data?.folderWorkflows ??
      EMPTY_ARRAY) as readonly ReferencesContext_FolderWorkflow[],
    genericStatuses: (data?.genericStatuses ??
      EMPTY_ARRAY) as readonly ReferencesContext_GenericStatus[],
    layerSources: (data?.layerSources ??
      EMPTY_ARRAY) as readonly ReferencesContext_LayerSource[],
    materialFileCategories: (data?.materialFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_MaterialFileCategory[],
    missionCategories: (data?.missionCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_MissionCategory[],
    missionFileCategories: (data?.missionFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_MissionFileCategory[],
    missionStates: (data?.missionStates ??
      EMPTY_ARRAY) as readonly ReferencesContext_MissionState[],
    missionWorkflows: (data?.missionWorkflows ??
      EMPTY_ARRAY) as readonly ReferencesContext_MissionWorkflow[],
    numberConstraints: (data?.numberConstraints ??
      EMPTY_ARRAY) as readonly ReferencesContext_NumberConstraint[],
    organizationFunctions: (data?.organizationFunctions ??
      EMPTY_ARRAY) as readonly ReferencesContext_OrganizationFunction[],
    paletteModes: (data?.paletteModes ??
      EMPTY_ARRAY) as readonly ReferencesContext_PaletteMode[],
    pathologies: (data?.pathologies ??
      EMPTY_ARRAY) as readonly ReferencesContext_Pathology[],
    paymentMethods: (data?.paymentMethods ??
      EMPTY_ARRAY) as readonly ReferencesContext_PaymentMethod[],
    paymentTerms: (data?.paymentTerms ??
      EMPTY_ARRAY) as readonly ReferencesContext_PaymentTerm[],
    pennylaneUserAccounts: (data?.pennylaneUserAccounts ??
      EMPTY_ARRAY) as readonly ReferencesContext_PennylaneUserAccount[],
    postalAddressStandards: (data?.postalAddressStandards ??
      EMPTY_ARRAY) as readonly ReferencesContext_PostalAddressStandard[],
    externalReferenceTypes: (data?.externalReferenceTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_ExternalReferenceType[],
    printStyles: (data?.printStyles ??
      EMPTY_ARRAY) as readonly ReferencesContext_PrintStyle[],
    projections: (data?.projections ??
      EMPTY_ARRAY) as readonly ReferencesContext_Projection[],
    quotationFileCategories: (data?.quotationFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_QuotationFileCategory[],
    quotationStates: (data?.quotationStates ??
      EMPTY_ARRAY) as readonly ReferencesContext_QuotationState[],
    quotationWorkflows: (data?.quotationWorkflows ??
      EMPTY_ARRAY) as readonly ReferencesContext_QuotationWorkflow[],
    references: (data?.references ??
      EMPTY_ARRAY) as readonly ReferencesContext_Reference[],
    reportFileCategories: (data?.reportFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_ReportFileCategory[],
    reportStates: (data?.reportStates ??
      EMPTY_ARRAY) as readonly ReferencesContext_ReportState[],
    reportWorkflows: (data?.reportWorkflows ??
      EMPTY_ARRAY) as readonly ReferencesContext_ReportWorkflow[],
    representativeTypes: (data?.representativeTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_RepresentativeType[],
    resolvableMissionTypeFileCategories:
      (data?.resolvableMissionTypeFileCategories ??
        EMPTY_ARRAY) as readonly ReferencesContext_ResolvableMissionTypeFileCategory[],
    resolvableServiceFileCategories: (data?.resolvableServiceFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_ResolvableServiceFileCategory[],
    resolverGroups: (data?.resolverGroups ??
      EMPTY_ARRAY) as readonly ReferencesContext_ResolverGroup[],
    resolverReturnTypes: (data?.resolverReturnTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_ResolverReturnType[],
    resolvers: (data?.resolvers ??
      EMPTY_ARRAY) as readonly ReferencesContext_Resolver[],
    sectionTemplateFileCategories: (data?.sectionTemplateFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_SectionTemplateFileCategory[],
    sectionTypes: (data?.sectionTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_SectionType[],
    storageStrategies: (data?.storageStrategies ??
      EMPTY_ARRAY) as readonly ReferencesContext_StorageStrategy[],
    thirdPartyCategories: (data?.thirdPartyCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_ThirdPartyCategory[],
    thirdPartyFileCategories: (data?.thirdPartyFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_ThirdPartyFileCategory[],
    thirdPartyTypes: (data?.thirdPartyTypes ??
      EMPTY_ARRAY) as readonly ReferencesContext_ThirdPartyType[],
    timeUnits: (data?.timeUnits ??
      EMPTY_ARRAY) as readonly ReferencesContext_TimeUnit[],
    toolkits: (data?.toolkits ??
      EMPTY_ARRAY) as readonly ReferencesContext_Toolkit[],
    units: (data?.units ?? EMPTY_ARRAY) as readonly ReferencesContext_Unit[],
    userActions: (data?.userActions ??
      EMPTY_ARRAY) as readonly ReferencesContext_UserAction[],
    userFileCategories: (data?.userFileCategories ??
      EMPTY_ARRAY) as readonly ReferencesContext_UserFileCategory[],
    userRoles: (data?.userRoles ??
      EMPTY_ARRAY) as readonly ReferencesContext_UserRole[],
    validityPeriods: (data?.validityPeriods ??
      EMPTY_ARRAY) as readonly ReferencesContext_ValidityPeriod[],
    vats: (data?.vats ?? EMPTY_ARRAY) as readonly ReferencesContext_Vat[],
    workflowFamilies: data?.workflowFamilies ?? EMPTY_ARRAY,
  };

  const memoizedContext = useMemoCompare(context, isEqual);

  const contextIsReady = isSuccess && isReadyToRender;

  const mappedContext = useMemo(() => {
    const [
      accountingStatesCodeMap,
      accountingStatesIdMap,
      defaultAccountingState,
    ] = referencesMapper(memoizedContext.accountingStates);

    const [
      accountingWorkflowsCodeMap,
      accountingWorkflowsIdMap,
      defaultAccountingWorkflow,
    ] = referencesMapper(memoizedContext.accountingWorkflows);

    const [actorCategoriesCodeMap, actorCategoriesIdMap, defaultActorCategory] =
      referencesMapper(memoizedContext.actorCategories);

    const [actorTypesCodeMap, actorTypesIdMap, defaultActorType] =
      referencesMapper(memoizedContext.actorTypes);

    const [addressTypesCodeMap, addressTypesIdMap, defaultAddressType] =
      referencesMapper(memoizedContext.addressTypes);

    const [
      addressableConfigsCodeMap,
      addressableConfigsIdMap,
      defaultAddressableConfig,
    ] = referencesMapper(memoizedContext.addressableConfigs);

    const [archiveBindersCodeMap, archiveBindersIdMap, defaultArchiveBinder] =
      referencesMapper(memoizedContext.archiveBinders);

    const [
      businessEventFileCategoriesCodeMap,
      businessEventFileCategoriesIdMap,
      defaultBusinessEventFileCategory,
    ] = referencesMapper(memoizedContext.businessEventFileCategories);

    const [
      businessEventStatesCodeMap,
      businessEventStatesIdMap,
      defaultBusinessEventState,
    ] = referencesMapper(memoizedContext.businessEventStates);

    const [
      businessEventTypesCodeMap,
      businessEventTypesIdMap,
      defaultBusinessEventType,
    ] = referencesMapper(memoizedContext.businessEventTypes);

    const [
      businessEventVenuesCodeMap,
      businessEventVenuesIdMap,
      defaultBusinessEventVenue,
    ] = referencesMapper(memoizedContext.businessEventVenues);

    const [
      businessEventWorkflowsCodeMap,
      businessEventWorkflowsIdMap,
      defaultBusinessEventWorkflow,
    ] = referencesMapper(memoizedContext.businessEventWorkflows);

    const [civilitiesCodeMap, civilitiesIdMap, defaultCivility] =
      referencesMapper(memoizedContext.civilities);

    const [
      codingStrategiesCodeMap,
      codingStrategiesIdMap,
      defaultCodingStrategy,
    ] = referencesMapper(memoizedContext.codingStrategies);

    const [
      constructionTypesCodeMap,
      constructionTypesIdMap,
      defaultConstructionType,
    ] = referencesMapper(memoizedContext.constructionTypes);

    const [contentOriginsCodeMap, contentOriginsIdMap, defaultContentOrigin] =
      referencesMapper(memoizedContext.contentOrigins);

    const [contextsCodeMap, contextsIdMap, defaultContext] = referencesMapper(
      memoizedContext.contexts,
    );

    const [
      costFileCategoriesCodeMap,
      costFileCategoriesIdMap,
      defaultCostFileCategory,
    ] = referencesMapper(memoizedContext.costFileCategories);

    const [costMarginsCodeMap, costMarginsIdMap, defaultCostMargin] =
      referencesMapper(memoizedContext.costMargins);

    const [costTypesCodeMap, costTypesIdMap, defaultCostType] =
      referencesMapper(memoizedContext.costTypes);

    const [currenciesCodeMap, currenciesIdMap, defaultCurrency] =
      referencesMapper(memoizedContext.currencies);

    const [destinationsCodeMap, destinationsIdMap, defaultDestination] =
      referencesMapper(memoizedContext.destinations);

    const [distanceRadiusCodeMap, distanceRadiusIdMap, defaultDistanceRadius] =
      referencesMapper(memoizedContext.distanceRadius);

    const [
      documentTemplateFileCategoriesCodeMap,
      documentTemplateFileCategoriesIdMap,
      defaultDocumentTemplateFileCategory,
    ] = referencesMapper(memoizedContext.documentTemplateFileCategories);

    const [documentTypesCodeMap, documentTypesIdMap, defaultDocumentType] =
      referencesMapper(memoizedContext.documentTypes);

    const [edmDomainsCodeMap, edmDomainsIdMap, defaultEdmDomain] =
      referencesMapper(memoizedContext.edmDomains);

    const [
      entityRelationshipStrategiesCodeMap,
      entityRelationshipStrategiesIdMap,
      defaultEntityRelationshipStrategy,
    ] = referencesMapper(memoizedContext.entityRelationshipStrategies);

    const [
      fileDirectoryCategoriesCodeMap,
      fileDirectoryCategoriesIdMap,
      defaultFileDirectoryCategory,
    ] = referencesMapper(memoizedContext.fileDirectoryCategories);

    const [
      fileDirectoryTypesCodeMap,
      fileDirectoryTypesIdMap,
      defaultFileDirectoryType,
    ] = referencesMapper(memoizedContext.fileDirectoryTypes);

    const [fileOriginsCodeMap, fileOriginsIdMap, defaultFileOrigin] =
      referencesMapper(memoizedContext.fileOrigins);

    const [
      folderFileCategoriesCodeMap,
      folderFileCategoriesIdMap,
      defaultFolderFileCategory,
    ] = referencesMapper(memoizedContext.folderFileCategories);

    const [folderStatesCodeMap, folderStatesIdMap, defaultFolderState] =
      referencesMapper(memoizedContext.folderStates);

    const [
      folderWorkflowsCodeMap,
      folderWorkflowsIdMap,
      defaultFolderWorkflow,
    ] = referencesMapper(memoizedContext.folderWorkflows);

    const [genericStatusesCodeMap, genericStatusesIdMap, defaultGenericStatus] =
      referencesMapper(memoizedContext.genericStatuses);

    const [layerSourcesCodeMap, layerSourcesIdMap, defaultLayerSource] =
      referencesMapper(memoizedContext.layerSources);

    const [
      materialFileCategoriesCodeMap,
      materialFileCategoriesIdMap,
      defaultMaterialFileCategory,
    ] = referencesMapper(memoizedContext.materialFileCategories);

    const [
      missionCategoriesCodeMap,
      missionCategoriesIdMap,
      defaultMissionCategory,
    ] = referencesMapper(memoizedContext.missionCategories);

    const [
      missionFileCategoriesCodeMap,
      missionFileCategoriesIdMap,
      defaultMissionFileCategory,
    ] = referencesMapper(memoizedContext.missionFileCategories);

    const [missionStatesCodeMap, missionStatesIdMap, defaultMissionState] =
      referencesMapper(memoizedContext.missionStates);

    const [
      missionWorkflowsCodeMap,
      missionWorkflowsIdMap,
      defaultMissionWorkflow,
    ] = referencesMapper(memoizedContext.missionWorkflows);

    const [
      numberConstraintsCodeMap,
      numberConstraintsIdMap,
      defaultNumberConstraint,
    ] = referencesMapper(memoizedContext.numberConstraints);

    const [
      organizationFunctionsCodeMap,
      organizationFunctionsIdMap,
      defaultOrganizationFunction,
    ] = referencesMapper(memoizedContext.organizationFunctions);

    const [paletteModesCodeMap, paletteModesIdMap, defaultPaletteMode] =
      referencesMapper(memoizedContext.paletteModes);

    const [pathologiesCodeMap, pathologiesIdMap, defaultPathology] =
      referencesMapper(memoizedContext.pathologies);

    const [paymentMethodsCodeMap, paymentMethodsIdMap, defaultPaymentMethod] =
      referencesMapper(memoizedContext.paymentMethods);

    const [paymentTermsCodeMap, paymentTermsIdMap, defaultPaymentTerm] =
      referencesMapper(memoizedContext.paymentTerms);

    const [
      pennylaneUserAccountsCodeMap,
      pennylaneUserAccountsIdMap,
      defaultPennylaneUserAccount,
    ] = referencesMapper(memoizedContext.pennylaneUserAccounts);

    const [
      postalAddressStandardsCodeMap,
      postalAddressStandardsIdMap,
      defaultPostalAddressStandard,
    ] = referencesMapper(memoizedContext.postalAddressStandards);
    const [
      externalReferenceTypesCodeMap,
      externalReferenceTypesIdMap,
      defaultExternalReferenceType,
    ] = referencesMapper(memoizedContext.externalReferenceTypes);

    const [printStylesCodeMap, printStylesIdMap, defaultPrintStyle] =
      referencesMapper(memoizedContext.printStyles);

    const [projectionsCodeMap, projectionsIdMap, defaultProjection] =
      referencesMapper(memoizedContext.projections);

    const [
      quotationFileCategoriesCodeMap,
      quotationFileCategoriesIdMap,
      defaultQuotationFileCategory,
    ] = referencesMapper(memoizedContext.quotationFileCategories);

    const [
      quotationStatesCodeMap,
      quotationStatesIdMap,
      defaultQuotationState,
    ] = referencesMapper(memoizedContext.quotationStates);

    const [
      quotationWorkflowsCodeMap,
      quotationWorkflowsIdMap,
      defaultQuotationWorkflow,
    ] = referencesMapper(memoizedContext.quotationWorkflows);

    const [referencesCodeMap, referencesIdMap, defaultReference] =
      referencesMapper(memoizedContext.references);

    const [
      reportFileCategoriesCodeMap,
      reportFileCategoriesIdMap,
      defaultReportFileCategory,
    ] = referencesMapper(memoizedContext.reportFileCategories);

    const [reportStatesCodeMap, reportStatesIdMap, defaultReportState] =
      referencesMapper(memoizedContext.reportStates);

    const [
      reportWorkflowsCodeMap,
      reportWorkflowsIdMap,
      defaultReportWorkflow,
    ] = referencesMapper(memoizedContext.reportWorkflows);

    const [
      representativeTypesCodeMap,
      representativeTypesIdMap,
      defaultRepresentativeType,
    ] = referencesMapper(memoizedContext.representativeTypes);

    const [
      resolvableMissionTypeFileCategoriesCodeMap,
      resolvableMissionTypeFileCategoriesIdMap,
      defaultResolvableMissionTypeFileCategory,
    ] = referencesMapper(memoizedContext.resolvableMissionTypeFileCategories);

    const [
      resolvableServiceFileCategoriesCodeMap,
      resolvableServiceFileCategoriesIdMap,
      defaultResolvableServiceFileCategory,
    ] = referencesMapper(memoizedContext.resolvableServiceFileCategories);

    const [resolverGroupsCodeMap, resolverGroupsIdMap, defaultResolverGroup] =
      referencesMapper(memoizedContext.resolverGroups);

    const [
      resolverReturnTypesCodeMap,
      resolverReturnTypesIdMap,
      defaultResolverReturnType,
    ] = referencesMapper(memoizedContext.resolverReturnTypes);

    const [resolversCodeMap, resolversIdMap, defaultResolver] =
      referencesMapper(memoizedContext.resolvers);

    const [
      sectionTemplateFileCategoriesCodeMap,
      sectionTemplateFileCategoriesIdMap,
      defaultSectionTemplateFileCategory,
    ] = referencesMapper(memoizedContext.sectionTemplateFileCategories);

    const [sectionTypesCodeMap, sectionTypesIdMap, defaultSectionType] =
      referencesMapper(memoizedContext.sectionTypes);

    const [
      storageStrategiesCodeMap,
      storageStrategiesIdMap,
      defaultStorageStrategy,
    ] = referencesMapper(memoizedContext.storageStrategies);

    const [
      thirdPartyCategoriesCodeMap,
      thirdPartyCategoriesIdMap,
      defaultThirdPartyCategory,
    ] = referencesMapper(memoizedContext.thirdPartyCategories);

    const [
      thirdPartyFileCategoriesCodeMap,
      thirdPartyFileCategoriesIdMap,
      defaultThirdPartyFileCategory,
    ] = referencesMapper(memoizedContext.thirdPartyFileCategories);

    const [
      thirdPartyTypesCodeMap,
      thirdPartyTypesIdMap,
      defaultThirdPartyType,
    ] = referencesMapper(memoizedContext.thirdPartyTypes);

    const [timeUnitsCodeMap, timeUnitsIdMap, defaultTimeUnit] =
      referencesMapper(memoizedContext.timeUnits);

    const [toolkitsCodeMap, toolkitsIdMap, defaultToolkit] = referencesMapper(
      memoizedContext.toolkits,
    );

    const [unitsCodeMap, unitsIdMap, defaultUnit] = referencesMapper(
      memoizedContext.units,
    );

    const [userActionsCodeMap, userActionsIdMap, defaultUserAction] =
      referencesMapper(memoizedContext.userActions);

    const [
      userFileCategoriesCodeMap,
      userFileCategoriesIdMap,
      defaultUserFileCategory,
    ] = referencesMapper(memoizedContext.userFileCategories);

    const [userRolesCodeMap, userRolesIdMap, defaultUserRole] =
      referencesMapper(memoizedContext.userRoles);

    const [
      validityPeriodsCodeMap,
      validityPeriodsIdMap,
      defaultValidityPeriod,
    ] = referencesMapper(memoizedContext.validityPeriods);

    const [vatsCodeMap, vatsIdMap, defaultVat] = referencesMapper(
      memoizedContext.vats,
    );

    return {
      ...memoizedContext,
      accountingStatesCodeMap,
      accountingStatesIdMap,
      defaultAccountingState,
      accountingWorkflowsCodeMap,
      accountingWorkflowsIdMap,
      defaultAccountingWorkflow,
      actorCategoriesCodeMap,
      actorCategoriesIdMap,
      defaultActorCategory,
      actorTypesCodeMap,
      actorTypesIdMap,
      defaultActorType,
      addressTypesCodeMap,
      addressTypesIdMap,
      defaultAddressType,
      addressableConfigsCodeMap,
      addressableConfigsIdMap,
      defaultAddressableConfig,
      archiveBindersCodeMap,
      archiveBindersIdMap,
      defaultArchiveBinder,
      businessEventFileCategoriesCodeMap,
      businessEventFileCategoriesIdMap,
      defaultBusinessEventFileCategory,
      businessEventStatesCodeMap,
      businessEventStatesIdMap,
      defaultBusinessEventState,
      businessEventTypesCodeMap,
      businessEventTypesIdMap,
      defaultBusinessEventType,
      businessEventVenuesCodeMap,
      businessEventVenuesIdMap,
      defaultBusinessEventVenue,
      businessEventWorkflowsCodeMap,
      businessEventWorkflowsIdMap,
      defaultBusinessEventWorkflow,
      civilitiesCodeMap,
      civilitiesIdMap,
      defaultCivility,
      codingStrategiesCodeMap,
      codingStrategiesIdMap,
      defaultCodingStrategy,
      constructionTypesCodeMap,
      constructionTypesIdMap,
      defaultConstructionType,
      contentOriginsCodeMap,
      contentOriginsIdMap,
      defaultContentOrigin,
      contextsCodeMap,
      contextsIdMap,
      defaultContext,
      costFileCategoriesCodeMap,
      costFileCategoriesIdMap,
      defaultCostFileCategory,
      costMarginsCodeMap,
      costMarginsIdMap,
      defaultCostMargin,
      costTypesCodeMap,
      costTypesIdMap,
      defaultCostType,
      currenciesCodeMap,
      currenciesIdMap,
      defaultCurrency,
      destinationsCodeMap,
      destinationsIdMap,
      defaultDestination,
      distanceRadiusCodeMap,
      distanceRadiusIdMap,
      defaultDistanceRadius,
      documentTemplateFileCategoriesCodeMap,
      documentTemplateFileCategoriesIdMap,
      defaultDocumentTemplateFileCategory,
      documentTypesCodeMap,
      documentTypesIdMap,
      defaultDocumentType,
      edmDomainsCodeMap,
      edmDomainsIdMap,
      defaultEdmDomain,
      entityRelationshipStrategiesCodeMap,
      entityRelationshipStrategiesIdMap,
      defaultEntityRelationshipStrategy,
      fileDirectoryCategoriesCodeMap,
      fileDirectoryCategoriesIdMap,
      defaultFileDirectoryCategory,
      fileDirectoryTypesCodeMap,
      fileDirectoryTypesIdMap,
      defaultFileDirectoryType,
      fileOriginsCodeMap,
      fileOriginsIdMap,
      defaultFileOrigin,
      folderFileCategoriesCodeMap,
      folderFileCategoriesIdMap,
      defaultFolderFileCategory,
      folderStatesCodeMap,
      folderStatesIdMap,
      defaultFolderState,
      folderWorkflowsCodeMap,
      folderWorkflowsIdMap,
      defaultFolderWorkflow,
      genericStatusesCodeMap,
      genericStatusesIdMap,
      defaultGenericStatus,
      layerSourcesCodeMap,
      layerSourcesIdMap,
      defaultLayerSource,
      materialFileCategoriesCodeMap,
      materialFileCategoriesIdMap,
      defaultMaterialFileCategory,
      missionCategoriesCodeMap,
      missionCategoriesIdMap,
      defaultMissionCategory,
      missionFileCategoriesCodeMap,
      missionFileCategoriesIdMap,
      defaultMissionFileCategory,
      missionStatesCodeMap,
      missionStatesIdMap,
      defaultMissionState,
      missionWorkflowsCodeMap,
      missionWorkflowsIdMap,
      defaultMissionWorkflow,
      numberConstraintsCodeMap,
      numberConstraintsIdMap,
      defaultNumberConstraint,
      organizationFunctionsCodeMap,
      organizationFunctionsIdMap,
      defaultOrganizationFunction,
      paletteModesCodeMap,
      paletteModesIdMap,
      defaultPaletteMode,
      pathologiesCodeMap,
      pathologiesIdMap,
      defaultPathology,
      paymentMethodsCodeMap,
      paymentMethodsIdMap,
      defaultPaymentMethod,
      paymentTermsCodeMap,
      paymentTermsIdMap,
      defaultPaymentTerm,
      pennylaneUserAccountsCodeMap,
      pennylaneUserAccountsIdMap,
      defaultPennylaneUserAccount,
      postalAddressStandardsCodeMap,
      postalAddressStandardsIdMap,
      defaultPostalAddressStandard,
      externalReferenceTypesCodeMap,
      externalReferenceTypesIdMap,
      defaultExternalReferenceType,
      printStylesCodeMap,
      printStylesIdMap,
      defaultPrintStyle,
      projectionsCodeMap,
      projectionsIdMap,
      defaultProjection,
      quotationFileCategoriesCodeMap,
      quotationFileCategoriesIdMap,
      defaultQuotationFileCategory,
      quotationStatesCodeMap,
      quotationStatesIdMap,
      defaultQuotationState,
      quotationWorkflowsCodeMap,
      quotationWorkflowsIdMap,
      defaultQuotationWorkflow,
      referencesCodeMap,
      referencesIdMap,
      defaultReference,
      reportFileCategoriesCodeMap,
      reportFileCategoriesIdMap,
      defaultReportFileCategory,
      reportStatesCodeMap,
      reportStatesIdMap,
      defaultReportState,
      reportWorkflowsCodeMap,
      reportWorkflowsIdMap,
      defaultReportWorkflow,
      representativeTypesCodeMap,
      representativeTypesIdMap,
      defaultRepresentativeType,
      resolvableMissionTypeFileCategoriesCodeMap,
      resolvableMissionTypeFileCategoriesIdMap,
      defaultResolvableMissionTypeFileCategory,
      resolvableServiceFileCategoriesCodeMap,
      resolvableServiceFileCategoriesIdMap,
      defaultResolvableServiceFileCategory,
      resolverGroupsCodeMap,
      resolverGroupsIdMap,
      defaultResolverGroup,
      resolverReturnTypesCodeMap,
      resolverReturnTypesIdMap,
      defaultResolverReturnType,
      resolversCodeMap,
      resolversIdMap,
      defaultResolver,
      sectionTemplateFileCategoriesCodeMap,
      sectionTemplateFileCategoriesIdMap,
      defaultSectionTemplateFileCategory,
      sectionTypesCodeMap,
      sectionTypesIdMap,
      defaultSectionType,
      storageStrategiesCodeMap,
      storageStrategiesIdMap,
      defaultStorageStrategy,
      thirdPartyCategoriesCodeMap,
      thirdPartyCategoriesIdMap,
      defaultThirdPartyCategory,
      thirdPartyFileCategoriesCodeMap,
      thirdPartyFileCategoriesIdMap,
      defaultThirdPartyFileCategory,
      thirdPartyTypesCodeMap,
      thirdPartyTypesIdMap,
      defaultThirdPartyType,
      timeUnitsCodeMap,
      timeUnitsIdMap,
      defaultTimeUnit,
      toolkitsCodeMap,
      toolkitsIdMap,
      defaultToolkit,
      unitsCodeMap,
      unitsIdMap,
      defaultUnit,
      userActionsCodeMap,
      userActionsIdMap,
      defaultUserAction,
      userFileCategoriesCodeMap,
      userFileCategoriesIdMap,
      defaultUserFileCategory,
      userRolesCodeMap,
      userRolesIdMap,
      defaultUserRole,
      validityPeriodsCodeMap,
      validityPeriodsIdMap,
      defaultValidityPeriod,
      vatsCodeMap,
      vatsIdMap,
      defaultVat,
    };
  }, [memoizedContext]);

  if (contextIsReady) {
    return (
      <ReferencesContext.Provider value={mappedContext}>
        {children}
      </ReferencesContext.Provider>
    );
  }

  return <Loading />;
};

export { ReferencesContext, ReferencesContextProvider };
